import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE } from '@/src/infrastructure/lib/api';
import { auth } from '@/src/infrastructure/config/firebase';
import { TypeBRenderer, SceneManifest, TextValues } from '@/src/modules/renderer/TypeBRenderer';

// ─── Local helper components ──────────────────────────────────────────────────

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'outline' | 'danger';
  children: React.ReactNode;
}

function Button({ variant = 'primary', className = '', children, ...props }: ButtonProps) {
  const baseClass = "flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-xs font-bold transition-all active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none uppercase tracking-wider";
  const variantClass =
    variant === 'primary' ? "bg-electric-violet text-white shadow-lg shadow-electric-violet/20 hover:bg-electric-violet/90"
    : variant === 'danger' ? "bg-red-500 text-white hover:bg-red-600"
    : "border border-electric-violet/30 text-electric-violet hover:bg-electric-violet/10 bg-transparent";
  return (
    <button className={`${baseClass} ${variantClass} ${className}`} {...props}>
      {children}
    </button>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface DesignReport {
  palette: string[];
  mood: string[];
  regions: { role: string; bbox: number[]; desc: string }[];
  textBlocks: { role: string; text: string; style?: string }[];
  motionCandidates: { region: string; suggestion: string }[];
}

interface StaticLayer { id: string; from: string; z: number; fullBleed?: boolean; anchor?: string }
interface TextSlot { key: string; z: number; default?: string; editable?: boolean; required?: boolean; dynamic?: string }
interface AnimationEntry { target: string; type: string; trigger: string; params: Record<string, unknown> }

interface LayerPlan { staticLayers: StaticLayer[]; textSlots: TextSlot[] }
interface AnimationPlan { animations: AnimationEntry[] }

interface AnalyzeResponse {
  refId: string;
  themeName: string;
  designReport: DesignReport;
  layerPlan: LayerPlan;
  animationPlan: AnimationPlan;
}

interface GenerateResponse {
  generationId: string | null;
  manifest: SceneManifest;
  plan: { layerPlan: LayerPlan; animationPlan: AnimationPlan };
  meta: { durationMs: number; dryRun: boolean; provider: string; environment: string };
}

interface LibraryItem {
  id: string;
  themeName: string;
  environment: string;
  dryRun: boolean;
  provider: string | null;
  durationMs: number | null;
  templateCreated: boolean;
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  designReport?: DesignReport | null;
  referenceImageUrl?: string | null;
}

interface LibraryDetail extends LibraryItem {
  manifest: SceneManifest | null;
  plan: { layerPlan: LayerPlan; animationPlan: AnimationPlan } | null;
}

// ─── API helpers ─────────────────────────────────────────────────────────────

async function getAuthHeader(): Promise<Record<string, string>> {
  const isBypass = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';
  const token = isBypass ? 'dev-token' : await auth.currentUser?.getIdToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(path: string, body: FormData): Promise<T> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE}/${path}`, { method: 'POST', headers, body });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status}: ${text}`);
  }
  const json = await res.json();
  return (json?.data ?? json) as T;
}

async function apiGet<T>(path: string): Promise<T> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE}/${path}`, { method: 'GET', headers });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status}: ${text}`);
  }
  const json = await res.json();
  return (json?.data ?? json) as T;
}

async function apiPatch<T>(path: string, body: object): Promise<T> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE}/${path}`, {
    method: 'PATCH',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status}: ${text}`);
  }
  const json = await res.json();
  return (json?.data ?? json) as T;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function PaletteChip({ hex }: { hex: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-5 h-5 rounded border border-border" style={{ background: hex }} />
      <span className="text-xs text-on-surface-variant font-mono">{hex}</span>
    </div>
  );
}

function Tag({ label, variant = 'default' }: { label: string; variant?: 'default' | 'green' | 'blue' | 'red' | 'yellow' }) {
  const cls: Record<string, string> = {
    default: 'bg-primary/10 text-primary',
    green: 'bg-green-100 text-green-700',
    blue: 'bg-blue-100 text-blue-700',
    red: 'bg-red-100 text-red-700',
    yellow: 'bg-yellow-100 text-yellow-700',
  };
  return <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${cls[variant]}`}>{label}</span>;
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border border-border rounded-xl p-4 space-y-3">
      <h3 className="text-sm font-semibold text-on-surface">{title}</h3>
      {children}
    </div>
  );
}

function AnimBadge({ type, trigger }: { type: string; trigger: string }) {
  const colours: Record<string, string> = {
    bloom: 'bg-green-100 text-green-700',
    sway: 'bg-blue-100 text-blue-700',
    glow: 'bg-yellow-100 text-yellow-700',
    drift: 'bg-purple-100 text-purple-700',
    'fade-in': 'bg-gray-100 text-gray-700',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colours[type] ?? 'bg-surface-low text-on-surface-variant'}`}>
      {type} <span className="opacity-60">· {trigger}</span>
    </span>
  );
}

function EnvBadge({ env, dryRun }: { env: string; dryRun: boolean }) {
  return (
    <span className="flex items-center gap-1 flex-wrap">
      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${env === 'production' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
        {env}
      </span>
      {dryRun && <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-yellow-100 text-yellow-700">dry-run</span>}
    </span>
  );
}

// ─── Library View ─────────────────────────────────────────────────────────────

interface LibraryViewProps {
  onFinishedResume: (result: GenerateResponse) => void;
}

function LibraryView({ onFinishedResume }: LibraryViewProps) {
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<LibraryDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [patchingId, setPatchingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await apiGet<{ items: LibraryItem[]; total: number }>('type-b/library?limit=50');
      setItems(res.items);
      setTotal(res.total);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load library');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id: string) => {
    setDetailLoading(true); setError('');
    try {
      const detail = await apiGet<LibraryDetail>(`type-b/library/${id}`);
      setSelected(detail);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const patchItem = async (id: string, update: { templateCreated?: boolean; notes?: string }) => {
    setPatchingId(id);
    try {
      const updated = await apiPatch<LibraryItem>(`type-b/library/${id}`, update);
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...updated } : it)));
      if (selected?.id === id) setSelected((s) => s ? { ...s, ...updated } : s);
    } catch (e: any) {
      setError(e.message ?? 'Failed to update');
    } finally {
      setPatchingId(null);
    }
  };

  const handleResume = async () => {
    if (!selected) return;
    setDetailLoading(true); setError('');
    try {
      const result = await apiFetch<GenerateResponse>(`type-b/generate?resumeId=${selected.id}`, new FormData());
      onFinishedResume(result);
    } catch (e: any) {
      setError(e.message ?? 'Resume generation failed');
    } finally {
      setDetailLoading(false);
    }
  };

  if (selected) {
    const isResumable = !selected.manifest;

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="outline" className="py-2 px-4" onClick={() => setSelected(null)}>← Library</Button>
          <div>
            <h2 className="text-lg font-bold text-on-surface">{selected.themeName}</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <EnvBadge env={selected.environment} dryRun={selected.dryRun} />
              <span className="text-xs text-on-surface-variant">{new Date(selected.createdAt).toLocaleString()}</span>
              {selected.provider && <span className="text-xs text-on-surface-variant">· {selected.provider}</span>}
              {isResumable && <Tag label="Resumable" variant="yellow" />}
            </div>
          </div>
        </div>

        {/* templateCreated checkbox */}
        <SectionCard title="Status">
          <label className="flex items-center gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={selected.templateCreated}
              onChange={(e) => patchItem(selected.id, { templateCreated: e.target.checked })}
              disabled={patchingId === selected.id}
              className="w-4 h-4 rounded accent-electric-violet"
            />
            <span className="text-sm text-on-surface group-hover:text-primary transition-colors">
              Template created from this generation
            </span>
            {selected.templateCreated && <Tag label="Done" variant="green" />}
          </label>

          <div className="mt-2">
            <label className="block text-xs font-medium text-on-surface mb-1">Notes</label>
            <NoteEditor
              initial={selected.notes ?? ''}
              onSave={(notes) => patchItem(selected.id, { notes })}
              saving={patchingId === selected.id}
            />
          </div>
        </SectionCard>

        {selected.referenceImageUrl && (
          <SectionCard title="Original Reference Image">
            <div className="max-w-xs overflow-hidden rounded-lg border border-border bg-surface-low p-2">
              <img
                src={selected.referenceImageUrl}
                alt="Reference card"
                className="w-full h-auto max-h-64 object-contain mx-auto rounded-md"
              />
            </div>
          </SectionCard>
        )}

        {isResumable ? (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20 text-on-surface text-sm space-y-2">
              <p className="font-semibold text-yellow-600 dark:text-yellow-500">⚠️ Intermediate Analysis Saved</p>
              <p className="text-xs text-on-surface-variant">
                This generation did not complete the asset creation stage (or failed due to model/timeout issues).
                You can resume the layout generation using the saved AI design report without re-running the vision model.
              </p>
              <Button
                onClick={handleResume}
                disabled={detailLoading}
                className="mt-2 w-full sm:w-auto bg-yellow-500 text-white shadow-none hover:bg-yellow-600"
              >
                {detailLoading ? 'Resuming…' : '⚡ Resume Generation'}
              </Button>
            </div>

            {selected.designReport && (
              <div className="grid md:grid-cols-2 gap-4">
                <SectionCard title="Saved Design Report">
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-on-surface-variant mb-1.5">Palette</p>
                      <div className="flex flex-wrap gap-2">
                        {selected.designReport.palette.map((hex) => <PaletteChip key={hex} hex={hex} />)}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-on-surface-variant mb-1.5">Mood</p>
                      <div className="flex flex-wrap gap-1">
                        {selected.designReport.mood.map((m) => <Tag key={m} label={m} />)}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-on-surface-variant mb-1">Regions ({selected.designReport.regions.length})</p>
                      {selected.designReport.regions.map((r, idx) => (
                        <div key={idx} className="text-xs text-on-surface py-0.5">
                          <span className="font-medium">{r.role}</span> — {r.desc}
                        </div>
                      ))}
                    </div>
                    <div>
                      <p className="text-xs text-on-surface-variant mb-1">Text blocks ({selected.designReport.textBlocks.length})</p>
                      {selected.designReport.textBlocks.map((tb, idx) => (
                        <div key={idx} className="text-xs text-on-surface py-0.5">
                          <span className="font-medium">{tb.role}</span>: <span className="italic">"{tb.text}"</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </SectionCard>

                <SectionCard title="Metadata">
                  <div className="text-xs text-on-surface-variant space-y-1">
                    <div className="flex justify-between"><span>Theme Name</span><span className="font-mono text-on-surface">{selected.themeName}</span></div>
                    <div className="flex justify-between"><span>Status</span><Tag label="Resumable" variant="yellow" /></div>
                    <div className="flex justify-between"><span>Created At</span><span>{new Date(selected.createdAt).toLocaleString()}</span></div>
                  </div>
                </SectionCard>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Manifest inspector */}
            <SectionCard title="Manifest">
              <div className="text-xs text-on-surface-variant space-y-1">
                <div className="flex justify-between"><span>Theme</span><span className="font-mono text-on-surface">{selected.manifest!.themeName}</span></div>
                <div className="flex justify-between"><span>Assets</span><span>{selected.manifest!.assets.length}</span></div>
                <div className="flex justify-between"><span>Layers</span><span>{selected.manifest!.layers.length}</span></div>
                <div className="flex justify-between"><span>Text slots</span><span>{selected.manifest!.textSlots.length}</span></div>
                <div className="flex justify-between"><span>Bindings</span><span>{selected.manifest!.bindings.length}</span></div>
                {selected.durationMs && <div className="flex justify-between"><span>Generation time</span><span>{selected.durationMs}ms</span></div>}
              </div>

              <Button
                variant="outline"
                className="w-full text-xs mt-2 py-2"
                onClick={() => {
                  const blob = new Blob([JSON.stringify(selected.manifest, null, 2)], { type: 'application/json' });
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(blob);
                  a.download = `${selected.manifest!.themeName}-manifest.json`;
                  a.click();
                }}
              >
                ⬇ Download manifest.json
              </Button>
            </SectionCard>

            {/* Plan */}
            {selected.plan && (
              <SectionCard title="Layer & Animation Plan">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-medium text-on-surface mb-1">Static layers ({selected.plan.layerPlan.staticLayers.length})</p>
                    {selected.plan.layerPlan.staticLayers.map((l) => (
                      <div key={l.id} className="flex items-center gap-2 text-xs py-0.5">
                        <span className="font-mono text-primary w-6 text-right">{l.z}</span>
                        <span className="font-medium">{l.id}</span>
                        {l.fullBleed && <Tag label="fullBleed" />}
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="text-xs font-medium text-on-surface mb-1">Animations ({selected.plan.animationPlan.animations.length})</p>
                    {selected.plan.animationPlan.animations.map((a, i) => (
                      <div key={i} className="py-0.5">
                        <span className="text-xs font-medium">{a.target}: </span>
                        <AnimBadge type={a.type} trigger={a.trigger} />
                      </div>
                    ))}
                  </div>
                </div>
              </SectionCard>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-on-surface">Generation Library</h2>
          <p className="text-xs text-on-surface-variant mt-0.5">{total} total generations saved</p>
        </div>
        <Button variant="outline" className="py-2 px-4 text-xs" onClick={load}>↻ Refresh</Button>
      </div>

      {error && <div className="p-3 rounded-lg bg-status-failed/10 border border-status-failed/20 text-status-failed text-sm">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-16 gap-3 text-on-surface-variant">
          <div className="w-6 h-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          <span className="text-sm">Loading…</span>
        </div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border rounded-xl p-12 text-center text-on-surface-variant text-sm">
          No generations yet. Run a generate in the Studio tab to save one.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <div
              key={item.id}
              className="border border-border rounded-xl p-4 flex items-start gap-4 hover:border-primary/40 transition-colors"
            >
              {/* Checkbox */}
              <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={item.templateCreated}
                  onChange={(e) => patchItem(item.id, { templateCreated: e.target.checked })}
                  disabled={patchingId === item.id}
                  title="Mark as template created"
                  className="w-4 h-4 rounded accent-electric-violet cursor-pointer"
                />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openDetail(item.id)}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm text-on-surface">{item.themeName}</span>
                  <EnvBadge env={item.environment} dryRun={item.dryRun} />
                  {!item.durationMs && <Tag label="Resumable" variant="yellow" />}
                  {item.templateCreated && <Tag label="Template created" variant="green" />}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-on-surface-variant flex-wrap">
                  <span>{new Date(item.createdAt).toLocaleString()}</span>
                  {item.provider ? <span>· {item.provider}</span> : <span>· analysis only</span>}
                  {item.durationMs && <span>· {item.durationMs}ms</span>}
                  {item.notes && <span className="italic">· "{item.notes.slice(0, 60)}{item.notes.length > 60 ? '…' : ''}"</span>}
                </div>
              </div>

              {/* Open detail */}
              <button
                onClick={() => openDetail(item.id)}
                disabled={detailLoading}
                className="text-xs text-primary hover:underline shrink-0 disabled:opacity-50"
              >
                View →
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NoteEditor({ initial, onSave, saving }: { initial: string; onSave: (v: string) => void; saving: boolean }) {
  const [val, setVal] = useState(initial);
  const dirty = val !== initial;
  return (
    <div className="flex gap-2">
      <textarea
        value={val}
        onChange={(e) => setVal(e.target.value)}
        rows={2}
        placeholder="Add notes about this generation…"
        className="flex-1 px-2 py-1.5 rounded-lg border border-border bg-surface text-on-surface text-xs focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
      />
      {dirty && (
        <button
          onClick={() => onSave(val)}
          disabled={saving}
          className="px-3 py-1.5 rounded-lg bg-primary text-on-primary text-xs font-bold disabled:opacity-50"
        >
          {saving ? '…' : 'Save'}
        </button>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

type Step = 'upload' | 'plan' | 'generate' | 'preview';
type MainView = 'studio' | 'library';

export function TypeBStudioPage() {
  const [mainView, setMainView] = useState<MainView>('studio');

  // Studio state
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [themeName, setThemeName] = useState('');

  const [step, setStep] = useState<Step>('upload');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [analyzeResult, setAnalyzeResult] = useState<AnalyzeResponse | null>(null);
  const [generateResult, setGenerateResult] = useState<GenerateResponse | null>(null);
  const [textValues, setTextValues] = useState<TextValues>({});

  const handleFinishedResume = useCallback((result: GenerateResponse) => {
    setGenerateResult(result);
    const defaults: TextValues = {};
    for (const slot of result.manifest.textSlots) {
      if (slot.default) defaults[slot.key] = defaults[slot.key] ?? slot.default;
    }
    setTextValues((v) => ({ ...defaults, ...v }));
    setStep('preview');
    setMainView('studio');
  }, []);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setError('');
  };

  const handleAnalyze = useCallback(async () => {
    if (!file || !themeName.trim()) { setError('Please select an image and enter a theme name.'); return; }
    setLoading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('themeName', themeName.trim());
      const result = await apiFetch<AnalyzeResponse>(`type-b/analyze`, fd);
      setAnalyzeResult(result);
      const defaults: TextValues = {};
      for (const slot of result.layerPlan.textSlots) {
        if (slot.default) defaults[slot.key] = slot.default;
      }
      setTextValues(defaults);
      setStep('plan');
    } catch (e: any) {
      setError(e.message ?? 'Analyze failed');
    } finally {
      setLoading(false);
    }
  }, [file, themeName]);

  const handleGenerate = useCallback(async () => {
    if (!file || !themeName.trim()) return;
    setLoading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('themeName', themeName.trim());
      const result = await apiFetch<GenerateResponse>(`type-b/generate`, fd);
      setGenerateResult(result);
      const defaults: TextValues = {};
      for (const slot of result.manifest.textSlots) {
        if (slot.default) defaults[slot.key] = defaults[slot.key] ?? slot.default;
      }
      setTextValues((v) => ({ ...defaults, ...v }));
      setStep('preview');
    } catch (e: any) {
      setError(e.message ?? 'Generate failed');
    } finally {
      setLoading(false);
    }
  }, [file, themeName]);

  const reset = () => {
    setStep('upload'); setFile(null); setPreviewUrl(null);
    setThemeName(''); setError('');
    setAnalyzeResult(null); setGenerateResult(null); setTextValues({});
  };

  return (
    <div className="flex-1 flex flex-col min-h-screen bg-surface-lowest">
      <div className="mb-4">
        <h1 className="text-3xl font-bold text-on-surface">Type B Studio</h1>
        <p className="text-sm text-on-surface-variant mt-2">
          Upload a reference invitation → review the AI plan → generate reusable animated assets.
        </p>
      </div>

      {/* ── Tab bar ── */}
      <div className="flex gap-1 border-b border-border mb-6">
        {(['studio', 'library'] as MainView[]).map((v) => (
          <button
            key={v}
            onClick={() => setMainView(v)}
            className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              mainView === v
                ? 'border-primary text-primary'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            {v === 'studio' ? '⚗️ Studio' : '📚 Library'}
          </button>
        ))}
      </div>

      <div className="px-6 pb-8 max-w-6xl mx-auto w-full">

        {/* ════ LIBRARY VIEW ════ */}
        {mainView === 'library' && <LibraryView onFinishedResume={handleFinishedResume} />}

        {/* ════ STUDIO VIEW ════ */}
        {mainView === 'studio' && (
          <div className="space-y-6">

            {/* Step indicator */}
            <div className="flex items-center gap-2 text-sm">
              {(['upload', 'plan', 'generate', 'preview'] as Step[]).map((s, i) => (
                <React.Fragment key={s}>
                  <span className={`px-3 py-1 rounded-full font-medium ${step === s ? 'bg-primary text-on-primary' : 'text-on-surface-variant'}`}>
                    {i + 1}. {s.charAt(0).toUpperCase() + s.slice(1)}
                  </span>
                  {i < 3 && <span className="text-on-surface-variant">→</span>}
                </React.Fragment>
              ))}
              {step !== 'upload' && (
                <button onClick={reset} className="ml-auto text-xs text-on-surface-variant underline hover:text-primary">Start over</button>
              )}
            </div>

            {/* Error banner */}
            {error && (
              <div className="p-3 rounded-lg bg-status-failed/10 border border-status-failed/20 text-status-failed text-sm">{error}</div>
            )}

            {/* ── STEP 1: UPLOAD ── */}
            {step === 'upload' && (
              <div className="grid md:grid-cols-2 gap-6">
                <div
                  className="border-2 border-dashed border-border rounded-xl p-8 flex flex-col items-center justify-center gap-4 cursor-pointer hover:border-primary/50 hover:bg-surface-low transition-colors"
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const f = e.dataTransfer.files[0];
                    if (f) { setFile(f); setPreviewUrl(URL.createObjectURL(f)); }
                  }}
                >
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFileChange} />
                  {previewUrl
                    ? <img src={previewUrl} alt="Preview" className="max-h-56 rounded-lg object-contain shadow" />
                    : (
                      <div className="flex flex-col items-center gap-2 text-on-surface-variant">
                        <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                        </svg>
                        <p className="text-sm font-medium">Drop reference image here</p>
                        <p className="text-xs">PNG, JPEG, WebP · max 25 MB</p>
                      </div>
                    )
                  }
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-on-surface mb-1">Theme name</label>
                    <input
                      type="text"
                      value={themeName}
                      onChange={(e) => setThemeName(e.target.value)}
                      placeholder="e.g. harekrishna-floral"
                      className="w-full px-3 py-2 rounded-lg border border-border bg-surface text-on-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                    <p className="text-xs text-on-surface-variant mt-1">Will be slugified. Used as the template identifier.</p>
                  </div>
                  <Button onClick={handleAnalyze} disabled={!file || !themeName.trim() || loading} className="w-full">
                    {loading ? 'Analyzing…' : '🔍 Analyze Reference Image'}
                  </Button>
                </div>
              </div>
            )}

            {/* ── STEP 2: PLAN GATE ── */}
            {step === 'plan' && analyzeResult && (
              <div className="space-y-6">
                <div className="grid md:grid-cols-3 gap-4">
                  <SectionCard title="Design Report">
                    <div>
                      <p className="text-xs text-on-surface-variant mb-1.5">Palette</p>
                      <div className="flex flex-wrap gap-2">
                        {analyzeResult.designReport.palette.map((hex) => <PaletteChip key={hex} hex={hex} />)}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-on-surface-variant mb-1.5">Mood</p>
                      <div className="flex flex-wrap gap-1">
                        {analyzeResult.designReport.mood.map((m) => <Tag key={m} label={m} />)}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-on-surface-variant mb-1">Regions ({analyzeResult.designReport.regions.length})</p>
                      {analyzeResult.designReport.regions.map((r) => (
                        <div key={r.role} className="text-xs text-on-surface py-0.5">
                          <span className="font-medium">{r.role}</span> — {r.desc}
                        </div>
                      ))}
                    </div>
                    <div>
                      <p className="text-xs text-on-surface-variant mb-1">Motion candidates</p>
                      {analyzeResult.designReport.motionCandidates.map((mc, i) => (
                        <div key={i} className="text-xs text-on-surface py-0.5">
                          <span className="font-medium">{mc.region}</span>: {mc.suggestion}
                        </div>
                      ))}
                    </div>
                  </SectionCard>

                  <SectionCard title="Layer Plan">
                    <div>
                      <p className="text-xs text-on-surface-variant mb-1">Static layers ({analyzeResult.layerPlan.staticLayers.length})</p>
                      <div className="space-y-1">
                        {analyzeResult.layerPlan.staticLayers.map((l) => (
                          <div key={l.id} className="flex items-center gap-2 text-xs">
                            <span className="font-mono text-primary w-6 text-right">{l.z}</span>
                            <span className="font-medium">{l.id}</span>
                            {l.fullBleed && <Tag label="fullBleed" />}
                            {l.anchor && <span className="text-on-surface-variant">{l.anchor}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-on-surface-variant mb-1">Text slots ({analyzeResult.layerPlan.textSlots.length})</p>
                      <div className="space-y-1">
                        {analyzeResult.layerPlan.textSlots.map((s) => (
                          <div key={s.key} className="flex items-center gap-1.5 text-xs">
                            <span className="font-medium">{s.key}</span>
                            {s.required && <Tag label="required" />}
                            {s.dynamic && <Tag label="dynamic" />}
                          </div>
                        ))}
                      </div>
                    </div>
                  </SectionCard>

                  <SectionCard title="Animation Plan">
                    {analyzeResult.animationPlan.animations.length === 0
                      ? <p className="text-xs text-on-surface-variant">No animations planned.</p>
                      : analyzeResult.animationPlan.animations.map((a, i) => (
                        <div key={i} className="flex flex-col gap-0.5">
                          <span className="text-xs font-medium">{a.target}</span>
                          <AnimBadge type={a.type} trigger={a.trigger} />
                        </div>
                      ))
                    }
                  </SectionCard>
                </div>

                <SectionCard title="Edit Text Slots (optional)">
                  <div className="grid sm:grid-cols-2 gap-3">
                    {analyzeResult.layerPlan.textSlots.map((slot) => (
                      <div key={slot.key}>
                        <label className="block text-xs font-medium text-on-surface mb-1">
                          {slot.key} {slot.required && <span className="text-status-failed">*</span>}
                          {slot.dynamic && <span className="text-xs text-on-surface-variant ml-1">(auto)</span>}
                        </label>
                        <input
                          type="text"
                          value={textValues[slot.key] ?? slot.default ?? ''}
                          onChange={(e) => setTextValues((v) => ({ ...v, [slot.key]: e.target.value }))}
                          disabled={!!slot.dynamic}
                          placeholder={slot.default}
                          className="w-full px-2 py-1.5 rounded-lg border border-border bg-surface text-on-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50"
                        />
                      </div>
                    ))}
                  </div>
                </SectionCard>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setStep('upload')}>← Back</Button>
                  <Button onClick={() => { setStep('generate'); handleGenerate(); }} disabled={loading} className="flex-1">
                    {loading ? 'Generating…' : '✨ Approve & Generate Assets'}
                  </Button>
                </div>
              </div>
            )}

            {/* ── STEP 3: GENERATING ── */}
            {step === 'generate' && (
              <div className="flex flex-col items-center justify-center py-24 gap-4 text-on-surface-variant">
                <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin" />
                <p className="text-sm font-medium">Running pipeline…</p>
                <p className="text-xs">Analyzing → Regenerating assets → Extracting alpha → Assembling manifest</p>
              </div>
            )}

            {/* ── STEP 4: PREVIEW ── */}
            {step === 'preview' && generateResult && (
              <div className="space-y-6">

                {/* Saved-to-library banner */}
                {generateResult.generationId ? (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm">
                    <span className="text-lg">✅</span>
                    <span>Saved to library — id: <code className="font-mono text-xs bg-green-100 px-1 rounded">{generateResult.generationId}</code></span>
                    <button
                      onClick={() => setMainView('library')}
                      className="ml-auto text-xs underline hover:no-underline text-green-700"
                    >
                      View in Library →
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm">
                    <span>⚠️ Could not save to library — the manifest is still available below.</span>
                  </div>
                )}

                <div className="grid md:grid-cols-[1fr_300px] gap-6">
                  {/* Live preview */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-on-surface">Live Preview</h3>
                    <div className="rounded-xl overflow-hidden border border-border shadow-sm bg-white max-w-sm mx-auto">
                      <TypeBRenderer
                        manifest={generateResult.manifest}
                        textValues={textValues}
                        className="w-full"
                      />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2 mt-2">
                      {generateResult.manifest.textSlots.filter((s) => s.editable && !s.dynamic).map((slot) => (
                        <div key={slot.key}>
                          <label className="block text-xs font-medium text-on-surface mb-0.5">{slot.key}</label>
                          <input
                            type="text"
                            value={textValues[slot.key] ?? slot.default ?? ''}
                            onChange={(e) => setTextValues((v) => ({ ...v, [slot.key]: e.target.value }))}
                            className="w-full px-2 py-1 rounded border border-border bg-surface text-on-surface text-xs focus:outline-none focus:ring-1 focus:ring-primary/40"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Manifest inspector */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-on-surface">Manifest</h3>
                    <div className="text-xs text-on-surface-variant space-y-1">
                      <div className="flex justify-between"><span>Theme</span><span className="font-mono text-on-surface">{generateResult.manifest.themeName}</span></div>
                      <div className="flex justify-between"><span>Assets</span><span>{generateResult.manifest.assets.length}</span></div>
                      <div className="flex justify-between"><span>Layers</span><span>{generateResult.manifest.layers.length}</span></div>
                      <div className="flex justify-between"><span>Text slots</span><span>{generateResult.manifest.textSlots.length}</span></div>
                      <div className="flex justify-between"><span>Bindings</span><span>{generateResult.manifest.bindings.length}</span></div>
                      <div className="flex justify-between"><span>Duration</span><span>{generateResult.meta.durationMs}ms</span></div>
                      <div className="flex justify-between"><span>Provider</span><span>{generateResult.meta.provider}</span></div>
                      <div className="flex justify-between"><span>Environment</span><span>{generateResult.meta.environment}</span></div>
                    </div>

                    <div>
                      <p className="text-xs font-medium text-on-surface mb-1">Animations</p>
                      {generateResult.manifest.bindings.map((b, i) => (
                        <div key={i} className="flex items-center gap-1.5 text-xs py-0.5">
                          <span className="font-medium w-20 truncate">{b.target}</span>
                          <AnimBadge type={b.type} trigger={b.params.trigger as string} />
                          {!b.supported && <span className="text-status-failed text-xs">unsupported</span>}
                        </div>
                      ))}
                    </div>

                    <Button
                      variant="outline"
                      className="w-full text-xs"
                      onClick={() => {
                        const blob = new Blob([JSON.stringify(generateResult.manifest, null, 2)], { type: 'application/json' });
                        const a = document.createElement('a');
                        a.href = URL.createObjectURL(blob);
                        a.download = `${generateResult.manifest.themeName}-manifest.json`;
                        a.click();
                      }}
                    >
                      ⬇ Download manifest.json
                    </Button>
                  </div>
                </div>

                <Button onClick={reset} variant="outline">← Create another template</Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
